import { CopyButton } from "@components/shared/CopyButton";
import {
  APPROVE_OUTREACH_DRAFT,
  MARK_DRAFT_SENT,
  REJECT_OUTREACH_DRAFT,
} from "@lib/graphql/outreach";
import { Linkedin, Mail, MessageSquare, ShieldAlert, X } from "lucide-react";
import { useState } from "react";
import { useMutation } from "urql";
import { BuyerCard } from "./BuyerCard";
import { JobPostingEvidenceModal } from "./JobPostingEvidenceModal";
import type { OutreachDraft } from "./types";

const CHANNEL_ICON: Record<string, React.ComponentType<{ size?: number }>> = {
  linkedin_connect: Linkedin,
  linkedin_message: MessageSquare,
  email: Mail,
};

interface Props {
  draft: OutreachDraft;
  onChanged?: () => void;
}

export function DraftRow({ draft, onChanged }: Props) {
  const [, approve] = useMutation(APPROVE_OUTREACH_DRAFT);
  const [, reject] = useMutation(REJECT_OUTREACH_DRAFT);
  const [, markSent] = useMutation(MARK_DRAFT_SENT);

  const [evidenceOpen, setEvidenceOpen] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const Icon = CHANNEL_ICON[draft.channel] ?? MessageSquare;
  const score = draft.jobPosting?.agencyLikelihoodScore ?? null;

  const handleApproveAndCopy = async () => {
    setBusy(true);
    const result = await approve({ input: { id: draft.id } });
    if (result.data?.approveOutreachDraft?.errors?.length === 0) {
      onChanged?.();
    }
    setBusy(false);
  };

  const handleMarkSent = async () => {
    setBusy(true);
    await markSent({ input: { id: draft.id } });
    onChanged?.();
    setBusy(false);
  };

  const handleReject = async () => {
    if (!reason.trim()) return;
    setBusy(true);
    const result = await reject({ input: { id: draft.id, reason } });
    if (result.data?.rejectOutreachDraft?.errors?.length === 0) {
      setRejecting(false);
      setReason("");
      onChanged?.();
    }
    setBusy(false);
  };

  return (
    <div className="rounded-lg bg-card p-4 shadow-sm ring-1 ring-ring">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <BuyerCard buyer={draft.buyer} leading={<Icon size={14} />} />
          <div className="mt-1 flex flex-wrap items-center gap-3 text-xs text-text-muted">
            <span>{draft.outreachProgramme.name}</span>
            {score !== null && (
              <button
                type="button"
                onClick={() => setEvidenceOpen(true)}
                className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-amber-800 hover:bg-amber-100 dark:bg-amber-400/20 dark:text-amber-200"
                title="View classifier evidence"
              >
                <ShieldAlert size={12} />
                Signal {score}/100
              </button>
            )}
            {draft.buyer.linkedinUrl && (
              <a
                href={draft.buyer.linkedinUrl}
                target="_blank"
                rel="noreferrer"
                className="text-text-muted hover:text-accent"
              >
                LinkedIn
              </a>
            )}
          </div>
        </div>
      </div>

      {draft.channel === "email" && draft.subject && (
        <div className="mt-3 text-sm font-medium text-text-secondary">Subject: {draft.subject}</div>
      )}

      <pre className="mt-3 max-h-48 overflow-y-auto whitespace-pre-wrap rounded bg-card-alt p-3 text-sm text-text-primary">
        {draft.body}
      </pre>

      {rejecting ? (
        <div className="mt-3 space-y-2">
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Why is this draft unsuitable? (fed back into future drafts for this programme)"
            rows={2}
            className="block w-full rounded-md border border-input-border bg-card px-3 py-2 text-sm"
          />
          <div className="flex gap-2">
            <button
              onClick={handleReject}
              disabled={busy || !reason.trim()}
              className="rounded-md bg-red-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
            >
              Reject
            </button>
            <button
              onClick={() => {
                setRejecting(false);
                setReason("");
              }}
              className="rounded-md border border-input-border px-3 py-1.5 text-sm text-text-secondary hover:bg-card-hover"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          {draft.state === "pending_review" && (
            <>
              <CopyButton
                getContent={() => ({ html: draft.body.replace(/\n/g, "<br>"), text: draft.body })}
                ariaLabel="Copy draft body"
                title="Copy body to clipboard"
                className="flex items-center gap-1 rounded-md bg-accent px-3 py-1.5 text-xs font-medium text-white hover:bg-accent-hover"
                size={12}
                withLabel
              />
              <button
                onClick={handleApproveAndCopy}
                disabled={busy}
                className="rounded-md border border-input-border bg-card px-3 py-1.5 text-xs font-medium text-text-primary hover:bg-card-hover disabled:opacity-50"
              >
                Approve
              </button>
              <button
                onClick={() => setRejecting(true)}
                disabled={busy}
                className="inline-flex items-center gap-1 rounded-md border border-input-border px-3 py-1.5 text-xs font-medium text-text-secondary hover:bg-card-hover disabled:opacity-50"
              >
                <X size={12} />
                Reject
              </button>
            </>
          )}
          {draft.state === "approved" && (
            <button
              onClick={handleMarkSent}
              disabled={busy}
              className="rounded-md bg-accent px-3 py-1.5 text-xs font-medium text-white hover:bg-accent-hover disabled:opacity-50"
            >
              Mark as Sent
            </button>
          )}
        </div>
      )}

      {draft.jobPosting?.rawText && (
        <JobPostingEvidenceModal
          open={evidenceOpen}
          onClose={() => setEvidenceOpen(false)}
          title={draft.jobPosting.title ?? "Job posting"}
          rawText={draft.jobPosting.rawText}
          evidence={draft.jobPosting.evidence ?? []}
          score={draft.jobPosting.agencyLikelihoodScore}
          posterName={draft.jobPosting.posterName}
          posterCompany={draft.jobPosting.posterCompany}
        />
      )}
    </div>
  );
}
